import {
  Box,
  Chip,
  Divider,
  FormControl,
  List,
  ListItem,
  Stack,
  SxProps,
} from "@mui/material";
import {
  DatePicker,
  DatePickerProps,
  PickersShortcutsItem,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { Controller, FieldValues, UseControllerProps } from "react-hook-form";
import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { InputTooltip } from "./InputTooltip";

type Props<T extends FieldValues> = {
  label: string;
  tooltip?: React.ReactNode;
  datePickerProps?: DatePickerProps<DateTime>;
  sx?: SxProps;
  disabled?: boolean;
  required?: boolean;
  hideErrors?: boolean;
  helperText?: string;
  endAdornment?: React.ReactNode;
  shortcuts?: PickersShortcutsItem<DateTime>[]; // optional date shortcuts rendered inside the picker
  clearable?: boolean; // whether the field can be cleared (defaults to !required)
} & UseControllerProps<T>;

export function DateField<T extends FieldValues>(props: Props<T>) {
  // 72px accounts for tooltip (24px) + calendar icon (24px) + padding/spacing
  // 48px accounts for just calendar icon (24px) + padding/spacing
  const slotWidth = props.tooltip ? 72 : 48;
  const clearable = props.clearable ?? !props.required;

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext?.getValues(props.name) as string | undefined;
    const displayValue = value
      ? DateTime.fromISO(value).toFormat("MM/dd/yyyy")
      : "";
    return <DisplayOnlyField text={displayValue} label={props.label} />;
  }

  return (
    <FormControl fullWidth>
      <Controller
        name={props.name}
        control={props.control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <>
              <DatePicker
                sx={props.sx}
                {...props.datePickerProps}
                label={props.label}
                value={value ? DateTime.fromISO(value) : null}
                onChange={(newValue) => onChange(newValue?.toISODate() ?? null)}
                disabled={props.disabled}
                slots={{
                  inputAdornment: (adornmentProps) => (
                    <Stack direction="row" alignItems="center">
                      {props.tooltip && (
                        <InputTooltip title={props.tooltip} placement="right" />
                      )}
                      {props.endAdornment}
                      {adornmentProps.children}
                    </Stack>
                  ),
                  ...(props.shortcuts && props.shortcuts.length > 0
                    ? { shortcuts: HorizontalShortcuts }
                    : {}),
                }}
                slotProps={{
                  ...props.datePickerProps?.slotProps,
                  ...(clearable ? { field: { clearable: true } } : {}),
                  textField: {
                    ...props.datePickerProps?.slotProps?.textField,
                    error: !!error,
                    required: props.required,
                    sx: {
                      "& .MuiInputLabel-shrink": {
                        width: "auto", // Full width when shrunk/floating
                      },
                      "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                        width: `calc(100% - ${slotWidth}px)`, // Limit width to avoid overlap with tooltip/date picker icons
                      },
                      ...(props.datePickerProps?.slotProps?.textField &&
                      "sx" in props.datePickerProps.slotProps.textField
                        ? props.datePickerProps.slotProps.textField.sx
                        : {}),
                    },
                  },
                  actionBar: {
                    ...props.datePickerProps?.slotProps?.actionBar,
                    actions: clearable ? ["clear", "accept"] : ["accept"],
                  },
                  ...(props.shortcuts && props.shortcuts.length > 0
                    ? {
                        shortcuts: {
                          items: props.shortcuts,
                        },
                      }
                    : {}),
                }}
              />
              <ErrorDisplay
                error={error}
                helperText={props.helperText}
                hideErrors={props.hideErrors}
              />
            </>
          );
        }}
      />
    </FormControl>
  );
}

function HorizontalShortcuts(props: PickersShortcutsProps<DateTime | null>) {
  const { items, onChange, isValid, changeImportance = "accept" } = props;

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const newValue = item.getValue({ isValid });

    return {
      label: item.label,
      onClick: () => {
        onChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box
      sx={{
        width: "100%",
        gridRow: 1,
        gridColumn: 2,
      }}
    >
      <List
        dense
        sx={(theme) => ({
          display: "flex",
          justifyContent: "center",
          px: theme.spacing(2),
          "& .MuiListItem-root": {
            width: "inherit",
            py: 0,
            px: theme.spacing(0.5),
          },
        })}
      >
        {resolvedItems.map((item) => {
          return (
            <ListItem key={item.label}>
              <Chip {...item} />
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
}
